<template>
  <div>
    <div v-if="loading" class="flex justify-center p-lg">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <div v-else-if="error" class="alert alert-danger">
      {{ errorMessage }}
    </div>
    <div v-else>
      <date-strip-picker
        v-model="selectedDate"
        :active-dates="activeDates"
        :loading-more="loadingMore"
        @load-more="onLoadMore"
      ></date-strip-picker>

      <div v-if="loadMoreErrorMessage" class="alert alert-danger m-b-md">
        {{ loadMoreErrorMessage }}
      </div>

      <!-- Events for selected date -->
      <div class="flex flex-column gap-sm">
        <h2 class="m-t-none">{{ formatWithMoment(selectedDate, 'dddd, MMMM D, YYYY') }}</h2>
        <div v-if="selectedDateEvents.length === 0" class="flex p-md gap-sm border border-solid border-gray-200 rounded-sm align-items-baseline">
          <i class="fa-solid fa-circle-exclamation text-red-700"></i>
          <div>
            <h3 class="m-y-none">No available times for {{ formatWithMoment(selectedDate, 'M/D') }}</h3>
          </div>
        </div>

        <div v-else v-for="event in selectedDateEvents" :key="event.id" class="border border-solid border-gray-200 bg-gray-50 rounded-sm p-m hover:bg-gray-100 mb-xs">
          <div v-if="!hasSlotsAvailable(event)">
            <div class="flex justify-space-between align-items-center">
              <div>
                <p class="text-gray-600 m-y-none">{{ formatWithMoment(event.start_at, 'h:mm A') }} - {{ formatWithMoment(event.end_at, 'h:mm A') }}</p>
                <p class="text-red-700 m-t-xs">Fully booked - no slots available</p>
              </div>
              <a @click.prevent="handleBookClick(event.id)" class="button button-primary" disabled>Select</a>
            </div>
          </div>
          <div v-else class="flex justify-space-between align-items-center">
            <div>
              <p class="text-gray-600 m-y-none">{{ formatWithMoment(event.start_at, 'h:mm A') }} - {{ formatWithMoment(event.end_at, 'h:mm A') }}</p>

              <!-- Billing Plan Information -->
              <div v-if="getBillingPlan(event)" class="text-gray-600 m-t-xs">
                <p class="m-y-none">{{ getBillingPlan(event).name }}</p>
                <p class="m-y-none">{{ formatAsCurrency(getSingleWrestlerTotal(event)) }}/lesson</p>
                <p class="m-y-none">{{ getBillingPlan(event).max_wrestler_count - getConfirmedWrestlerCount(event) }} slots open ({{ getBillingPlan(event).max_wrestler_count }} wrestlers max)</p>
              </div>

              <!-- Wrestler Information -->
              <div v-if="hasWrestlers(event)" class="m-t-sm">
                <div class="flex flex-column" style="row-gap: 4px;">
                  <!-- Confirmed Wrestlers -->
                  <div v-if="getWrestlersByStatus(event, 'confirmed').length > 0">
                    <div class="flex gap-x-xs align-items-center">
                      <i class="fa-solid fa-circle-check text-blue-700"></i>
                      <span class="text-blue-700 text-12">Confirmed</span>
                    </div>
                    <div v-for="booking in getWrestlersByStatus(event, 'confirmed')" :key="booking.id" class="ml-sm text-gray-600 text-12">
                      {{ formatWrestlerName(booking) }} <span v-if="hasWrestlerDetails(booking)" class="text-gray-700">({{ formatWrestlerDetails(booking) }})</span>
                    </div>
                  </div>

                  <!-- Invited Wrestlers -->
                  <div v-if="getWrestlersByStatus(event, 'invited').length > 0">
                    <div class="flex gap-x-xs align-items-center">
                      <i class="fa-regular fa-envelope text-yellow-700"></i>
                      <span class="text-yellow-700 text-12">Invited</span>
                    </div>
                    <div v-for="booking in getWrestlersByStatus(event, 'invited')" :key="booking.id" class="m-l-sm text-gray-600 text-12">
                      {{ formatWrestlerName(booking) }} <span v-if="hasWrestlerDetails(booking)" class="text-gray-700">({{ formatWrestlerDetails(booking) }})</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <a @click.prevent="handleBookClick(event.id)" class="btn btn-default" data-cy="book-slot">Select</a>
          </div>
        </div>

        <div v-if="selectedDateEvents.length === 0 && suggestedDates.length > 0">
          <button @click="showNextAvailableDate" class="btn btn-default btn-outline w-full flex justify-center align-items-center gap-sm">
            Next available time <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errorableMix } from './mixins/errorable_mix';
import { currencyMix } from '../../shared/currency_mix';
import { formattingMixin } from './mixins/formatting_mix';
import { privateLessonMix } from './mixins/private_lesson_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import DateStripPicker from './date_strip_picker.vue';

export default {
  name: 'coach-lesson-availability',
  components: { DateStripPicker },
  mixins: [errorableMix, currencyMix, formattingMixin, appEmbedMix, privateLessonMix],
  props: {
    coachId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingMore: false,
      loadMoreErrorMessage: '',
      coachEvents: [],
      selectedDate: moment(),
      suggestedDates: [],
    };
  },
  created() {
    this.fetchCoachPrivateLessonEvents();
  },
  computed: {
    // Derived rather than stored so newly-fetched events for the selected day
    // appear without any manual refresh bookkeeping.
    selectedDateEvents() {
      return this.getEventsForDate(this.selectedDate);
    },
    activeDates() {
      return _.uniq(this.coachEvents.map((event) => this.formatWithMoment(event.start_at, 'YYYY-MM-DD')));
    },
    lessonsGroupedByDate() {
      return _.groupBy(this.coachEvents, (event) => this.formatWithMoment(event.start_at, 'YYYY-MM-DD'));
    },
    lessonsGroupedByWeekAndDay() {
      // First group events by week
      const byWeek = {};

      this.coachEvents.forEach((event) => {
        // Get week start (Sunday) and end (Saturday) for the event
        const eventDate = moment(event.start_at);
        const weekStart = moment(eventDate).startOf('week').format('YYYY-MM-DD');
        const weekEnd = moment(eventDate).endOf('week').format('YYYY-MM-DD');
        const weekKey = `${weekStart} - ${weekEnd}`;

        if (!byWeek[weekKey]) {
          byWeek[weekKey] = [];
        }

        byWeek[weekKey].push(event);
      });

      // Then for each week, group by day
      const byWeekAndDay = {};

      Object.keys(byWeek).forEach((weekKey) => {
        byWeekAndDay[weekKey] = _.groupBy(byWeek[weekKey], (event) => this.formatWithMoment(event.start_at, 'YYYY-MM-DD'));
      });

      return byWeekAndDay;
    },
  },
  methods: {
    getWeekDisplayName(weekKey) {
      const [start, end] = weekKey.split(' - ');
      return `${this.formatWithMoment(start, 'MMM D')} - ${this.formatWithMoment(end, 'MMM D, YYYY')}`;
    },
    getDayDisplayName(dateKey) {
      return this.formatWithMoment(dateKey, 'dddd, MMMM D');
    },
    fetchCoachPrivateLessonEvents() {
      this.loading = true;
      // By default, this endpoint will return all events for the next 30 days, the start date is based on the minimum booking set on the team level
      const url = this.$apiService.coachPrivateLessonEventsUrl(this.coachId);

      this.$apiService.loadAllPages(url, 'events')
        .then((objects) => {
          this.coachEvents = objects;
          // Initialize suggested dates based on events with available slots
          this.suggestedDates = this.findSuggestedDates();
          // Set initial selected date - prefer today if events exist, otherwise the first available date
          this.initializeSelectedDate();
        })
        .catch((error) => {
          this.errorMessage = this.parseErrorResponse(error);
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchMoreCoachPrivateLessonEvents(startAt, endAt) {
      // Loads events for the extended window when the strip emits load-more
      this.loadingMore = true;
      this.loadMoreErrorMessage = '';
      const url = this.$apiService.coachPrivateLessonEventsUrl(
        this.coachId,
        startAt.toISOString(),
        endAt.toISOString(),
      );

      return this.$apiService.loadAllPages(url, 'events')
        .then((objects) => {
        // Merge new events with existing ones, deduping by event id
          const existingIds = new Set(this.coachEvents.map((e) => e.id));
          const newEvents = objects.filter((e) => !existingIds.has(e.id));
          this.coachEvents = [...this.coachEvents, ...newEvents];
          // Re-compute suggested dates so "next available" navigation includes the new window
          this.suggestedDates = this.findSuggestedDates();
        })
        .catch((error) => {
        // Scoped to the load-more action so existing availability stays visible.
          this.loadMoreErrorMessage = this.parseErrorResponse(error);
        })
        .finally(() => {
          this.loadingMore = false;
        });
    },
    initializeSelectedDate() {
      const today = moment();
      const hasTodayEvents = this.coachEvents.some((event) => moment(event.start_at).isSame(today, 'day'));

      if (hasTodayEvents) {
        this.selectedDate = today;
      } else if (this.suggestedDates.length > 0) {
        this.selectedDate = this.suggestedDates[0];
      } else if (this.coachEvents.length > 0) {
        this.selectedDate = moment(this.coachEvents[0].start_at);
      }
    },
    onLoadMore({ startAt, endAt }) {
      this.fetchMoreCoachPrivateLessonEvents(startAt, endAt);
    },
    handleBookClick(eventId) {
      if (this.forAppEmbed) {
        const url = `${window.location.origin}/app_embeds/events/${eventId}/book`;
        this.askAppToPush(url, 'Review & Confirm');
      } else {
        // Regular web navigation
        window.location.href = `/events/${eventId}/book`;
      }
    },
    getWrestlersByStatus(event, status) {
      if (!event.event_bookings) return [];
      return event.event_bookings.filter((booking) => booking.profile_type === 'WrestlerProfile' && booking.status === status);
    },

    formatWrestlerName(booking) {
      if (!booking.profile || !booking.profile.display_name) return 'Unknown';

      return booking.profile.display_name;
    },
    hasWrestlerDetails(booking) {
      return booking.profile && (
        booking.profile.date_of_birth
        || booking.profile.weight_class
        || booking.profile.experience
      );
    },
    formatWrestlerDetails(booking) {
      const details = [];

      // Add age if date of birth is available
      if (booking.profile.date_of_birth) {
        const birthDate = new Date(booking.profile.date_of_birth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        details.push(`${age}y`);
      }

      // Add weight class if available
      if (booking.profile.weight_class) {
        details.push(`${booking.profile.weight_class}lb`);
      }

      // Add experience if available
      if (booking.profile.experience) {
        details.push(this.formatExperience(booking.profile.experience));
      }

      return details.join(', ');
    },
    formatExperience(experience) {
      if (!experience) return '';

      // Convert experience value to readable format
      // Assuming experience is stored as a string like 'beginner', 'intermediate', etc.
      const experienceMap = {
        beginner: 'Beg',
        intermediate: 'Int',
        advanced: 'Adv',
        expert: 'Exp',
      };

      return experienceMap[experience.toLowerCase()] || experience;
    },
    getEventsForDate(date) {
      return this.coachEvents.filter((event) => moment(event.start_at).isSame(date, 'day'));
    },
    findSuggestedDates() {
      // Find dates that have available events (not fully booked)
      const availableDates = [];
      const dateMap = {};

      this.coachEvents.forEach((event) => {
        const eventDate = moment(event.start_at).startOf('day');
        const dateKey = eventDate.format('YYYY-MM-DD');

        if (!dateMap[dateKey] && this.hasSlotsAvailable(event)) {
          dateMap[dateKey] = true;
          availableDates.push(eventDate);
        }
      });

      return availableDates.sort((a, b) => a.valueOf() - b.valueOf());
    },
    showNextAvailableDate() {
      if (this.suggestedDates.length === 0) return;

      let nextDate = null;

      // Find the next date after the current selected date
      for (const date of this.suggestedDates) {
        if (date.isAfter(this.selectedDate, 'day')) {
          nextDate = date;
          break;
        }
      }

      // If no next date, loop back to the first one
      if (!nextDate && this.suggestedDates.length > 0) {
        nextDate = this.suggestedDates[0];
      }

      if (nextDate) {
        // The strip watches the selected date and re-anchors itself when the
        // date is outside its visible range.
        this.selectedDate = nextDate;
      }
    },
  },
};
</script>
